import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

const slideIn = keyframes`
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(100%);
  }
`;

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(66, 153, 225, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(66, 153, 225, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(66, 153, 225, 0);
  }
`;

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -100% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const fadeInOut = keyframes`
  0%, 100% {
    opacity: 0.4;
  }
  50% {
    opacity: 1;
  }
`;

const thinkingPulse = keyframes`
  0%, 100% {
    transform: scaleX(1);
    opacity: 0.5;
  }
  50% {
    transform: scaleX(1.05);
    opacity: 0.8;
  }
`;

export const FABContainer = styled.div<{ hasUnread: boolean }>`
  position: fixed;
  bottom: 77px; /* Tight stack with 53px button (Zendesk launcher ~76px) */
  right: 20px; /* Align with support messenger launcher offset */
  z-index: 999998; /* Just below Zendesk widget z-index (999999) */
`;

export const FABButton = styled.button<{ hasUnread: boolean }>`
  width: 53px;
  height: 53px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    transform: scale(1.08);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
  }

  &:active {
    transform: scale(0.95);
  }

  ${({ hasUnread }) =>
    hasUnread &&
    `
    animation: ${pulse} 2s infinite;

    &::after {
      content: '';
      position: absolute;
      top: 4px;
      right: 4px;
      width: 10px;
      height: 10px;
      background: #ff4757;
      border-radius: 50%;
      border: 2px solid white;
    }
  `}

  svg {
    width: 26px;
    height: 26px;
  }
`;

export const ChatPanelOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 999997; /* Below copilot panel and support messenger */
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
  transition: all 0.3s ease;
`;

export const ChatPanelContainer = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  width: 650px;
  background: white;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.1);
  z-index: 999998; /* Just below Zendesk widget z-index */
  display: flex;
  flex-direction: column;
  animation: ${({ isOpen }) => (isOpen ? slideIn : slideOut)} 0.3s ease forwards;
  visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};

  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
  }
`;

export const ChatHeader = styled.div`
  padding: 16px 24px;
  background: #f3f4f6;
  color: #374151;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
`;

export const ChatTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  h2 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
  }

  .status-indicator {
    width: 8px;
    height: 8px;
    background: #10b981;
    border-radius: 50%;
    animation: ${pulse} 2s ease-in-out infinite;
  }
`;

export const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const ChatMessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #f9fafb;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }
`;

export const MessageBubble = styled.div<{ sender: 'user' | 'assistant' }>`
  max-width: 85%;
  padding: ${({ sender }) => (sender === 'user' ? '12px 16px' : '16px')};
  border-radius: 18px;
  word-wrap: break-word;
  align-self: ${({ sender }) => (sender === 'user' ? 'flex-end' : 'flex-start')};
  background: ${({ sender }) =>
    sender === 'user' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'white'};
  color: ${({ sender }) => (sender === 'user' ? 'white' : '#374151')};
  box-shadow: ${({ sender }) =>
    sender === 'user' ? '0 2px 8px rgba(102, 126, 234, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)'};
  animation: ${bounce} 0.5s ease;

  /* Style for user messages */
  ${({ sender }) =>
    sender === 'user' &&
    `
    p {
      margin: 0;
      line-height: 1.5;
    }
  `}

  /* Let formatted content handle its own styles for assistant messages */
  ${({ sender }) =>
    sender === 'assistant' &&
    `
    > div {
      &:first-child {
        > *:first-child {
          margin-top: 0;
        }
        > *:last-child {
          margin-bottom: 0;
        }
      }
    }
  `}
`;

export const MessageTime = styled.span`
  font-size: 11px;
  opacity: 0.7;
  margin-top: 4px;
  display: block;
`;

export const ThinkingText = styled.div`
  font-size: 14px;
  color: #667eea;
  font-weight: 500;
  animation: ${fadeInOut} 2s ease-in-out infinite;
`;

export const TypingIndicator = styled.div`
  display: flex;
  flex-direction: column;
  padding: 14px 18px;
  background: white;
  border-radius: 18px;
  width: fit-content;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

export const ChatInputContainer = styled.div`
  padding: 20px;
  background: white;
  border-top: 1px solid #e5e7eb;
`;

export const ChatInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f3f4f6;
  border-radius: 2px;
  padding: 8px 8px 8px 20px;
  transition: all 0.2s ease;
  border: 2px solid transparent;

  &:focus-within {
    background: white;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

export const ChatInput = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  font-size: 15px;
  color: #374151;

  &::placeholder {
    color: #9ca3af;
  }
`;

export const SendButton = styled.button<{ disabled: boolean }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${({ disabled }) =>
    disabled ? '#e5e7eb' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  border: none;
  color: ${({ disabled }) => (disabled ? '#9ca3af' : 'white')};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    transform: scale(1.1);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const SuggestionsContainer = styled.div`
  padding: 20px 24px;
  background: white;
  border-top: 1px solid #e5e7eb;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

export const SuggestionChip = styled.button`
  padding: 10px 16px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 24px;
  font-size: 14px;
  font-weight: 500;
  color: #4b5563;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  line-height: 1.4;

  &:hover {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-color: transparent;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(102, 126, 234, 0.25);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  text-align: center;
  color: #6b7280;
  flex: 1;
  min-height: 0;

  svg {
    width: 64px;
    height: 64px;
    opacity: 0.5;
    margin-bottom: 16px;
  }

  h3 {
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 8px 0;
    color: #374151;
  }

  p {
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
  }
`;

export const WelcomeCard = styled.div`
  background: linear-gradient(135deg, #1e3a8a 0%, #60a5fa 100%);
  border-radius: 16px;
  padding: 48px 32px 32px;
  text-align: center;
  color: white;
  margin-bottom: 24px;

  h2 {
    font-size: 32px;
    font-weight: 600;
    margin: 0 0 12px 0;
    color: white;
  }

  p {
    font-size: 16px;
    margin: 0 0 24px 0;
    opacity: 0.95;
    font-weight: 400;
  }

  .description {
    font-size: 14px;
    line-height: 1.6;
    opacity: 0.9;
    margin: 0;
  }
`;
